import {
	CommunityAnnouncementStatus,
	CommunityAnnouncementType,
	DurationDetails,
	DurationDetailsInput,
	EmployerJobAdLocation,
	SalaryDetails,
} from "src/__generated__/graphql";
import { countriesWithDialingCodes } from "src/constants";

export const onSelectedFile = (
	event: any,
	setImage: (image: string) => void
) => {
	const file = event.target.files[0];

	const reader = new FileReader();
	reader.onload = (e) => {
		setImage(e.target?.result as string);
	};
	reader.readAsDataURL(file);
};

export const formatAsNaira = (amount: number, passedDecimalPlaces?: number) => {
	const decimalPlaces = passedDecimalPlaces ?? 2;

	return `₦${amount.toLocaleString("en-NG", {
		minimumFractionDigits: decimalPlaces,
		maximumFractionDigits: decimalPlaces,
	})}`;
};

export const formatAsCurrency = (amount: number, currencyCode: string) => {
	if (currencyCode === "NGN") {
		return formatAsNaira(amount);
	}

	if (currencyCode === "GBP" || currencyCode === "EUR") {
		return new Intl.NumberFormat("en-GB", {
			style: "currency",
			currency: currencyCode,
		}).format(amount);
	}

	if (currencyCode === "ZAR") {
		return new Intl.NumberFormat("en-ZA", {
			style: "currency",
			currency: currencyCode,
		}).format(amount);
	}

	if (currencyCode === "KES") {
		return new Intl.NumberFormat("en-KE", {
			style: "currency",
			currency: currencyCode,
		}).format(amount);
	}

	if (currencyCode === "GHS") {
		return new Intl.NumberFormat("en-GH", {
			style: "currency",
			currency: currencyCode,
		}).format(amount);
	}

	if (currencyCode === "CAD") {
		return new Intl.NumberFormat("en-CA", {
			style: "currency",
			currency: currencyCode,
		}).format(amount);
	}

	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currencyCode,
	}).format(amount);
};

export const getCappedAmount = (amount: number) => {
	const gates = [
		1000, 5000, 10000, 20000, 50000, 100000, 150000, 200000, 300000, 500000,
		1000000, 5000000, 10000000,
	];

	let lastGate = 0;

	for (const gate of gates) {
		if (amount < gate) {
			return lastGate;
		}

		lastGate = gate;
	}

	return lastGate;
};

export const isDate = (str: string): boolean => {
	const date = new Date(str);
	return !isNaN(date.getTime());
};

export const generateProShareId = (email: string) => {
	return `pro.${email.split("@")[0]}`;
};

export const removePhoneNumberPrefix = (phoneNumber: string) => {
	// iterate over each country with dialing code
	for (const country of countriesWithDialingCodes) {
		// constructing the regular expression dynamically to match the dialing code for each country
		const regex = new RegExp(`^\\+${country.value}`);
		// replace the prefix if it matches the dialing code
		phoneNumber = phoneNumber.replace(regex, "");
	}
	return phoneNumber;
};

export const extractPhoneNumberPrefix = (phoneNumber?: string) => {
	if (!phoneNumber) return undefined;
	for (const country of countriesWithDialingCodes) {
		const regex = new RegExp(`^\\+${country.value}`);
		if (regex.test(phoneNumber)) {
			// Return the matched dialing code
			return `+${country.value}`;
		}
	}

	return undefined; // Return undefined if no match is found
};

export const hasValidCharacters = (phoneNumberString: string) => {
	return /^[0-9]+$/.test(phoneNumberString);
};

export const cleanPhoneNumber = (phoneNumberString: string) => {
	let cleaned = phoneNumberString.trim();
	// remove braces, dashes and spaces
	cleaned = cleaned.replace(/-|\s|\(|\)/g, "");

	// remove the country code
	cleaned = removePhoneNumberPrefix(phoneNumberString);

	if (cleaned.startsWith("0")) {
		cleaned = cleaned.replace(/^0/, "");
	}

	return cleaned;
};

const phoneNumberValidatorMap: Record<
	string,
	(phoneNumber: string) => boolean
> = {
	"+234": (phoneNumber: string) => {
		let trimmedNumber = phoneNumber.trim();

		const cleaned = cleanPhoneNumber(trimmedNumber);

		if (!hasValidCharacters(cleaned)) {
			return false;
		}

		if (cleaned.length && cleaned.length === 10) {
			return true;
		}

		return false;
	},

	default: (phoneNumber: string) => {
		if (!hasValidCharacters(phoneNumber)) {
			return false;
		}

		return phoneNumber.length >= 8 && phoneNumber.length <= 15;
	},
};

export const isValidIntlPhoneNumber = (phoneNumberString: string) => {
	const prefix = extractPhoneNumberPrefix(phoneNumberString);

	if (!prefix) {
		return false;
	}

	const validator =
		phoneNumberValidatorMap[prefix] || phoneNumberValidatorMap.default;

	return validator(phoneNumberString);
};

export const scrollToTop = () => {
	// Find the main scrollable container (from LayoutV2)
	const mainContainer = document.querySelector("main.overflow-y-auto");
	if (mainContainer) {
		mainContainer.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	} else {
		// Fallback to window scroll
		window.scrollTo({ top: 0 });
	}
};

export const formatContractJobAdDuration = (duration: DurationDetailsInput) => {
	return duration.value > 1
		? `${duration.value} ${duration.type.toLowerCase()}`
		: `${duration.value} ${duration.type.toLowerCase().replace("s", "")}`;
};

export const formatLocation = (location: EmployerJobAdLocation): string => {
	return (
		[location.area, location.state, location.country]
			.filter(Boolean)
			.join(", ") || "N/A"
	);
};

export const formatDuration = (duration: DurationDetails): string => {
	return `${duration.value} ${duration.type.toLowerCase()}`;
};

export const formatEmploymentType = (employmentType: string): string => {
	switch (employmentType) {
		case "FULL_TIME":
			return "Full time";
		case "CONTRACT":
			return "Contract";
		default:
			return employmentType;
	}
};

// Job opportunity format utility functions
export const formatSalary = (salary: SalaryDetails): string => {
	if (salary.starting && salary.cap) {
		return `${formatAsCurrency(
			salary.starting,
			salary.currencyCode
		)} - ${formatAsCurrency(salary.cap, salary.currencyCode)}`;
	} else if (salary.starting) {
		return formatAsCurrency(salary.starting, salary.currencyCode);
	} else if (salary.cap) {
		return formatAsCurrency(salary.cap, salary.currencyCode);
	}

	return "N/A";
};

export const formatAnnouncementType = (
	announcementType: CommunityAnnouncementType
): string => {
	switch (announcementType) {
		case CommunityAnnouncementType.GeneralUpdate:
			return "General update";
		case CommunityAnnouncementType.JobOpportunity:
			return "Job opportunity";
		case CommunityAnnouncementType.TrainingEvent:
			return "Training event";
		default:
			return announcementType;
	}
};

export const formatAnnouncementStatus = (
	status: CommunityAnnouncementStatus
): string => {
	switch (status) {
		case CommunityAnnouncementStatus.Active:
			return "Active";
		case CommunityAnnouncementStatus.Archived:
			return "Archived";
		case CommunityAnnouncementStatus.Draft:
			return "Draft";
		default:
			return status;
	}
};

export const debounce = (func: Function, wait: number) => {
	let timeout: NodeJS.Timeout;
	return (...args: unknown[]) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => func(...args), wait);
	};
};